import * as React from "react";
import { FormControlLabel, Checkbox, TextField } from "@material-ui/core";
import { formatMessage } from "lib/globalize-util";
import { $, merge } from "glamor";
import { ISize } from "../../interfaces/dropdown-sizes-types";
import theme from "styles/theme";
import { KEYBOARD_KEYS } from "constants/keyboard";

export const BLANK = "--";

interface ICheckboxListNewItemProps {
    item: ISize;
    onTextChange: (item: ISize, createNewItem?: boolean) => void;
    onCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onItemAdded: () => void;
}

const styles = merge(
    $(" .MuiOutlinedInput-notchedOutline", {
        display: "none",
    }),
    $(" .MuiInputBase-input", {
        border: "none",
        borderBottom: `1px solid ${theme.palette.accent1Color}`,
        marginTop: "-23px",
        fontSize: "12px",
    }),
    $(" .MuiInputBase-input:focus", {
        boxSizing: "content-box",
    }),
    $(" .MuiCheckbox-colorSecondary.Mui-checked", {
        color: theme.palette.accent1Color,
    })
);

export const CheckboxListNewItem: React.FC<ICheckboxListNewItemProps> = ({
    item,
    onTextChange,
    onCheckboxChange,
    onItemAdded,
}) => {
    const [value, setValue] = React.useState<string>(item.title === BLANK ? "" : item.title);
    const valueRef = React.useRef(value);
    valueRef.current = value;

    const itemRef = React.useRef(item);
    itemRef.current = item;

    const handleTextFieldChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value.toString());
    }, []);
    const handleTextFieldBlur = React.useCallback(() => {
        onTextChange({ ...itemRef.current, title: valueRef.current.trim() });
    }, [onTextChange]);

    const divRef = React.useRef<HTMLDivElement>();
    const handleStopPropagationOnEnter = React.useCallback(
        (e: KeyboardEvent) => {
            if (e.key === KEYBOARD_KEYS.ENTER) {
                e.stopPropagation();
                onTextChange({ ...itemRef.current, title: valueRef.current.trim() }, true);
                setValue("");
                onItemAdded();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onTextChange]
    );

    React.useEffect(() => {
        divRef.current.addEventListener("keydown", handleStopPropagationOnEnter);
        return () => {
            divRef.current.removeEventListener("keydown", handleStopPropagationOnEnter); // eslint-disable-line react-hooks/exhaustive-deps
        };
    }, [onTextChange]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div {...styles} ref={divRef}>
            <FormControlLabel
                key={item.id}
                control={<Checkbox checked={item.selected} onChange={onCheckboxChange} value={item.title} />}
                label={
                    item.editable ? (
                        <TextField
                            name="size"
                            placeholder={formatMessage("Enter size")}
                            value={value}
                            autoFocus={true}
                            onChange={handleTextFieldChange}
                            onBlur={handleTextFieldBlur}
                        />
                    ) : (
                        value
                    )
                }
            />
        </div>
    );
};
